import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import MarkdownView from "../components/MarkdownView";
import { Calendar, User, MessageCircle, ChevronLeft, Share2 } from "lucide-react";
import { api } from "../api";
import { useSite } from "../context/SiteContext";

export default function BlogPost() {
  const { slug } = useParams();
  const { site } = useSite();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setNotFound(false);
    Promise.all([
      api.get(`/blog/${slug}`),
      api.get(`/blog`),
    ])
      .then(([p, all]) => {
        if (!active) return;
        setPost(p.data);
        setRelated((all.data || []).filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-slate-400">Loading...</div>;
  if (notFound || !post) {
    return (
      <div className="py-24 text-center">
        <h2 className="text-2xl font-bold text-white">Post not found</h2>
        <Link to="/blog" className="text-amber-400 mt-4 inline-block">← Back to Blog</Link>
      </div>
    );
  }

  const seo = post.seo || {};
  return (
    <div>
      <SEO
        title={seo.title || post.title}
        description={seo.description || post.excerpt}
        keywords={seo.keywords}
        ogImage={seo.ogImage || post.cover}
        canonical={seo.canonical}
        noindex={seo.noindex}
        jsonLd={seo.jsonLd}
        type="article"
      />
      <PageHero title={post.title} breadcrumb="Blog" subtitle={post.excerpt} />

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-amber-400 text-sm mb-6 hover:text-amber-300"><ChevronLeft className="w-4 h-4" /> Back to Blog</Link>

          {post.cover && (
            <div className="rounded-3xl overflow-hidden border border-amber-500/20 mb-8">
              <img src={post.cover} alt={post.title} className="w-full h-64 md:h-96 object-cover" />
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.date}</span>
              <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
            </div>
            <button onClick={() => navigator.share?.({ title: post.title, url: window.location.href }).catch(() => {})} className="inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300"><Share2 className="w-4 h-4" /> Share</button>
          </div>

          <article className="card-dark p-6 md:p-9">
            <MarkdownView>{post.body}</MarkdownView>
          </article>

          <div className="mt-10 card-dark p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-white font-bold text-lg">Ready to get your Cricket ID?</div>
              <div className="text-slate-400 text-sm">2 minute me activate · ₹100 se start</div>
            </div>
            <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp Now</a>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-white mb-6">Related Articles</h3>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((r) => (
                  <Link to={`/blog/${r.slug}`} key={r.slug} className="card-dark overflow-hidden group">
                    {r.cover && <img src={r.cover} alt={r.title} className="w-full h-32 object-cover" />}
                    <div className="p-4">
                      <div className="text-xs text-slate-400">{r.date}</div>
                      <div className="mt-1 text-sm font-semibold text-white group-hover:text-amber-300 line-clamp-2">{r.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
